import { Injectable } from '@angular/core';

import { BaseComponentResolver } from '@services/_base/base-component-resolver/base-component-resolver';

import { ParagraphCtaData } from '@generated/content';
import { CmsComponentConfig, Cta } from '@interfaces/config';
import { LinkObject } from '@interfaces/link-object';
import { TMediaNodeType } from '@interfaces/media';
import { CmsComponentNodeData } from '@models/interfaces';

@Injectable({ providedIn: 'root' })
export class CtaComponentResolver extends BaseComponentResolver {
  static override instance: CtaComponentResolver;
  private readonly componentType = Cta.TypeEnum;

  static getInstance(): CtaComponentResolver {
    return (CtaComponentResolver.instance ||= new CtaComponentResolver());
  }

  public override resolveComponentConfig(
    node: ParagraphCtaData,
    nodePageData: CmsComponentNodeData[] = null
  ): CmsComponentConfig<Cta.ComponentConfig> | null {
    if (!node) {
      return null;
    }

    this.nodePageData = nodePageData ?? [];

    const text = this.hyphenate(node.attributes.field_cta_text);
    const subText = this.hyphenate(node.attributes.field_cta_subtext);
    const link = CtaComponentResolver.extractLink(node);
    const image = this.getImage(node);
    const buttonType = this.getButtonType(node);
    const layout = this.getCtaLayout(node);
    const ctaBgColor = this.getCtaBgColor(node);

    return {
      componentType: this.componentType,
      config: {
        text,
        subText,
        link,
        image,
        buttonType,
        layout,
        ctaBgColor,
        nodeId: BaseComponentResolver.getNodeId(node),
      },
    };
  }

  private static extractLink(node: ParagraphCtaData): LinkObject | null {
    if (!node?.attributes?.field_link_cta) {
      return null;
    }

    const { title, url, uri } = node?.attributes?.field_link_cta;
    const href = url || uri;
 
      const external = href.startsWith('http');

    if (title && href) {
      return { label: this.hyphenate(title), href, external };
    }

    return null;
  }

  private getImage(node: ParagraphCtaData): { nodeId: string; url: string | null } {
    if (!node?.relationships?.field_media_cta?.data) {
      return null;
    }

    const { type, id } = node.relationships.field_media_cta.data;
    const mediaUris = this.getMediaDataUrls(id, type as TMediaNodeType) || {};
    const dataUrls = mediaUris.dataUrls || {};

    return {
      nodeId: id,
      url: dataUrls['webp'] || dataUrls['wide'] || dataUrls['medium'] || dataUrls['media_library'],
    };
  }

  private getCtaLayout(node: ParagraphCtaData): Cta.Layout {
    if (!node.attributes?.field_cta_layout) {
      return Cta.Layout.STANDARD;
    }
    switch (node.attributes.field_cta_layout) {
      case 'short':
        return Cta.Layout.SHORT;
      case 'standard':
        return Cta.Layout.STANDARD;
      default:
        return Cta.Layout.STANDARD;
    }
  }
  
  private getCtaBgColor(node: ParagraphCtaData): string {
    if (!node.attributes?.field_cta_background_color) {
      return null;
    }
    
    return node.attributes.field_cta_background_color.color;
  }
}
